/**
 * SettingsSystemPage — суперадмин-настройки.
 *
 * Block 2: 5 табов — «Провайдеры», «Пресеты», «Модули», «Module Configs», «Свойства и категории» (Master Templates).
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Key, Sliders, ListChecks, Cog, Library } from 'lucide-react';

import { useAdminSummary } from '@/hooks/useAdmin';
import { ApiKeysConfig } from '@/components/admin/ApiKeysConfig';
import { PresetsConfig } from '@/components/admin/PresetsConfig';
import { ModuleRegistryAdmin } from '@/components/admin/ModuleRegistryAdmin';
import { ModuleConfigsAdmin } from '@/components/admin/ModuleConfigsAdmin';
import { MasterTemplatesConfig } from '@/components/admin/MasterTemplatesConfig';
import { cn } from '@/lib/utils';

type TabKey = 'providers' | 'presets' | 'modules' | 'moduleConfigs' | 'masterTemplates';

const TABS: { key: TabKey; labelKey: string; icon: typeof Key }[] = [
  { key: 'providers', labelKey: 'system.tabs.providers', icon: Key },
  { key: 'presets', labelKey: 'system.tabs.presets', icon: Sliders },
  { key: 'modules', labelKey: 'system.tabs.modules', icon: ListChecks },
  { key: 'moduleConfigs', labelKey: 'system.tabs.moduleConfigs', icon: Cog },
  { key: 'masterTemplates', labelKey: 'system.tabs.masterTemplates', icon: Library },
];

export function SettingsSystemPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<TabKey>('providers');
  const { data: summary } = useAdminSummary();

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold mb-1">{t('system.title')}</h1>
        <p className="text-sm text-surface-400">{t('system.subtitle')}</p>
      </div>

      {/* Сводка */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <SummaryCard
            label={t('system.summary.providers')}
            value={summary.providers_total}
            sub={`${summary.providers_active} active`}
          />
          <SummaryCard
            label={t('system.summary.presets')}
            value={summary.presets_total}
            sub="economy / optimal / max"
          />
          <SummaryCard
            label={t('system.summary.modules')}
            value={summary.modules_total}
            sub={`${summary.modules_active} active`}
          />
          <SummaryCard
            label={t('system.summary.prompts')}
            value={summary.system_prompts_total}
            sub="ai_prompt + pdf"
          />
        </div>
      )}

      {/* Табы */}
      <div className="flex gap-1 border-b border-surface-800 overflow-x-auto">
        {TABS.map((tDef) => (
          <button
            key={tDef.key}
            onClick={() => setTab(tDef.key)}
            className={cn(
              'flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
              tab === tDef.key
                ? 'border-primary-500 text-primary-300'
                : 'border-transparent text-surface-400 hover:text-surface-200',
            )}
          >
            <tDef.icon className="w-4 h-4" />
            {t(tDef.labelKey)}
          </button>
        ))}
      </div>

      {/* Контент таба */}
      <div>
        {tab === 'providers' && <ApiKeysConfig />}
        {tab === 'presets' && <PresetsConfig />}
        {tab === 'modules' && <ModuleRegistryAdmin />}
        {tab === 'moduleConfigs' && <ModuleConfigsAdmin />}
        {tab === 'masterTemplates' && <MasterTemplatesConfig />}
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub }: { label: string; value: number; sub: string }) {
  return (
    <div className="card">
      <div className="text-xs text-surface-500 uppercase tracking-wider">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      <div className="text-xs text-surface-500 mt-1">{sub}</div>
    </div>
  );
}
